import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchAiLayouts } from "../store/actions/aiActions";

const STORAGE_KEY = "ai_layout_groups";

const slugify = value => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[×x]/g, "x")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const useLayoutGroup = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const transformLayouts = useCallback(data => {
    return Object.entries(data).map(([groupName, layouts]) => ({
      id: slugify(groupName),
      title: groupName,
      type: "group",
      children: Object.entries(layouts).map(([layoutName, categories]) => ({
        id: slugify(layoutName),
        title: layoutName,
        type: "layout",

        children: Object.entries(categories).map(([categoryName, templates]) => ({
          id: slugify(categoryName),
          title: categoryName,
          type: "category",

          children: templates.map(templateName => ({
            id: slugify(templateName),
            title: templateName,
            type: "template",
          })),
        })),
      })),
    }));
  }, []);

  const getLayouts = useCallback(
    async (forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        // Get from localStorage first
        if (!forceRefresh) {
          const cachedLayouts = localStorage.getItem(STORAGE_KEY);

          if (cachedLayouts) {
            return JSON.parse(cachedLayouts);
          }
        }

        // Fetch from API through Redux
        const response = await dispatch(fetchAiLayouts());

        // Convert API response
        // const layouts = transformLayouts(response.data);
        const layouts = response.data?.layout_groups ?? [];
        // Store transformed response
        localStorage.setItem(STORAGE_KEY, JSON.stringify(layouts));

        return layouts;
      } catch (err) {
        console.error("Failed to fetch AI layouts:", err);

        setError(err);

        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch, transformLayouts]
  );

  return {
    getLayouts,
    loading,
    error,
  };
};

export default useLayoutGroup;
